import React from "react";
import { Tooltip } from "antd";
import { getCategoryList } from "./compareConstants";
import "./CategorySummaryBar.css";

const InfoIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="16" x2="12" y2="12" />
    <line x1="12" y1="8" x2="12.01" y2="8" />
  </svg>
);

const Delta = ({ delta }) => {
  if (delta === null || delta === undefined) return null;
  if (delta === 0) return <span className="csb-card__delta csb-card__delta--zero">— no change</span>;
  const up = delta > 0;
  return (
    <span className={`csb-card__delta ${up ? "csb-card__delta--up" : "csb-card__delta--down"}`}>
      {up ? "▲" : "▼"} {up ? "+" : ""}{delta}
    </span>
  );
};

const KpiCard = ({ dot, label, count, delta, tooltip, isTotal }) => (
  <Tooltip
    title={tooltip}
    placement="bottom"
    overlayClassName="csb-card-tooltip"
  >
    <div className={`csb-card${isTotal ? " csb-card--total" : ""}`}>
      <div className="csb-card__header">
        {dot && <span className="csb-card__dot" style={{ background: dot }} />}
        <span className="csb-card__label">{label}</span>
      </div>
      <div className="csb-card__number">{(count || 0).toLocaleString()}</div>
      {isTotal
        ? <div className="csb-card__total-sub">In selected scope</div>
        : <Delta delta={delta} />
      }
    </div>
  </Tooltip>
);

const CategorySummaryBar = ({ summary, metric, varId }) => {
  if (!summary || !summary.categories) {
    return (
      <div className="csb-root csb-root--empty">
        <span className="csb-hint">Apply filters to see category summary</span>
      </div>
    );
  }

  const categories = getCategoryList(metric, varId);
  const { period_1, period_2, total, no_data_p2_only, no_data_p1_only } = summary;
  const metricLabel = metric.charAt(0).toUpperCase() + metric.slice(1);

  const buildCatTooltip = (cat, found) => {
    const p1 = found ? found.count_p1 : 0;
    const p2 = found ? found.count_p2 : 0;
    const d = found ? found.delta : 0;
    const deltaStr = d > 0 ? `▲ +${d}` : d < 0 ? `▼ ${d}` : "no change";
    const isGoodCategory = cat.rank === 0;
    const isBadCategory = cat.rank >= 2;
    let action = "";
    if (d > 0 && isGoodCategory) action = "More stations are performing well — a positive trend.";
    else if (d > 0 && isBadCategory) action = "More stations are in this problematic category — investigate.";
    else if (d < 0 && isGoodCategory) action = "Fewer stations in the best category — check what degraded.";
    else if (d < 0 && isBadCategory) action = "Fewer stations in this category — stations are improving.";
    else action = "No net movement — station mix is stable.";
    return `${cat.label}\n\n` +
      `P1: ${p1.toLocaleString()} stations → P2: ${p2.toLocaleString()} stations (${deltaStr})\n\n` +
      `The number ${p2.toLocaleString()} = stations in this category in Period 2.\n` +
      `The delta ${deltaStr} = net stations that moved ${d > 0 ? "into" : d < 0 ? "out of" : "neither in nor out of"} this category.\n\n` +
      action;
  };

  const buildStoppedTooltip = (count) => {
    return `Stopped reporting\n\n` +
      `${count.toLocaleString()} stations had data in P1 but none in P2.\n\n` +
      `These stations may have gone offline, been decommissioned, or lost connectivity.\n` +
      `Action: Check station operational status and GTS connectivity.`;
  };

  const buildTotalTooltip = (count) => {
    return `Total Stations\n\n` +
      `${count.toLocaleString()} stations are in the selected scope (region, country, variable, centres).\n\n` +
      `This is the universe of stations being compared. The sum of all category cards should equal this total.`;
  };

  return (
    <div className="csb-root">
      <div className="csb-heading">
        <strong>Category Summary for {metricLabel}</strong>
        {" "}(Period 2 vs Period 1 &nbsp;·&nbsp; P1: {period_1} → P2: {period_2})
        <Tooltip
          title="Each card shows how many stations are in that WDQMS category in Period 2, with the net change (▲▼) from Period 1. The delta tells you if stations moved into or out of that category."
          placement="bottom"
        >
          <span className="csb-info-trigger"><InfoIcon /></span>
        </Tooltip>
      </div>
      <div className="csb-cards">
        {categories.map(cat => {
          const found = summary.categories.find(c => c.rank === cat.rank);
          return (
            <KpiCard
              key={cat.rank}
              dot={cat.color}
              label={cat.label}
              count={found ? found.count_p2 : 0}
              delta={found ? found.delta : 0}
              tooltip={buildCatTooltip(cat, found)}
            />
          );
        })}

        {(no_data_p2_only > 0) && (
          <KpiCard
            dot="#1a1a1a"
            label="Stopped reporting"
            count={no_data_p2_only}
            tooltip={buildStoppedTooltip(no_data_p2_only)}
          />
        )}

        <KpiCard
          label="Total Stations"
          count={total}
          isTotal
          tooltip={buildTotalTooltip(total)}
        />
      </div>
    </div>
  );
};

export default CategorySummaryBar;
